// admin/pages/Referrals.tsx — Phase 3 referral oversight.
//
// View-only page over admin:referrals_query (paginated). Mutating action
// is admin:clear_referral_flag — the row's "Clear flag" button.
//
// Page header: total payouts (sum of monthly_bonus_turns_active across
// every referral) + flagged count, dovetailing with the DB-derived
// totalPayoutsTurns / flaggedCount fields returned by the query.

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import type { Socket } from "socket.io-client";
import { styles, tokens } from "../styles";
import { RpcError } from "../socket/rpc";
import type {
  AdminClearReferralFlagAck,
  AdminReferralRow,
  AdminReferralsQueryAck,
  AdminReferralStatus,
} from "../store/types";

interface ReferralsPageProps {
  socket: RefObject<Socket | null>;
}

const STATUS_OPTIONS: Array<{
  value: "all" | AdminReferralStatus | "flagged";
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "cancelled", label: "Cancelled" },
  { value: "flagged", label: "Flagged" },
];

const PAGE_SIZES = [25, 50, 100] as const;

export default function Referrals({ socket }: ReferralsPageProps) {
  const [query, setQuery] = useState<{
    q: string;
    status: "all" | AdminReferralStatus | "flagged";
    page: number;
    pageSize: (typeof PAGE_SIZES)[number];
  }>({
    q: "",
    status: "all",
    page: 1,
    pageSize: 25,
  });

  const [filterInput, setFilterInput] = useState("");
  useEffect(() => {
    const t = window.setTimeout(() => {
      const trimmed = filterInput.trim();
      if (trimmed !== query.q) {
        setQuery((q) => ({ ...q, q: trimmed, page: 1 }));
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [filterInput, query.q]);

  const [rows, setRows] = useState<AdminReferralRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPayoutsTurns, setTotalPayoutsTurns] = useState(0);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Run the query when any input changes. Stale-ack guard via requestId.
  useEffect(() => {
    const sock = socket.current;
    if (!sock) return;
    let cancelled = false;
    const requestId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await new Promise<{
          requestId: string;
          referrals: AdminReferralRow[];
          total: number;
          page: number;
          pageSize: number;
          totalPayoutsTurns: number;
          flaggedCount: number;
        }>((resolve, reject) => {
          if (!sock.connected) {
            reject(new RpcError("socket_not_connected"));
            return;
          }
          let settled = false;
          const timeout = window.setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new RpcError("timeout"));
          }, 15_000);
          sock.emit(
            "admin:referrals_query",
            {
              requestId,
              q: query.q || undefined,
              status: query.status,
              page: query.page,
              pageSize: query.pageSize,
            },
            (ack: AdminReferralsQueryAck) => {
              if (settled) return;
              settled = true;
              window.clearTimeout(timeout);
              if (ack.ok) {
                resolve(ack);
              } else {
                reject(new RpcError(ack.error));
              }
            },
          );
        });
        if (cancelled) return;
        if (result.requestId !== requestId) return; // stale ack
        setRows(result.referrals);
        setTotal(result.total);
        setTotalPayoutsTurns(result.totalPayoutsTurns);
        setFlaggedCount(result.flaggedCount);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [socket, query.q, query.status, query.page, query.pageSize]);

  const clearFlag = useCallback(
    async (referralId: string) => {
      const sock = socket.current;
      if (!sock) return;
      try {
        const ack = await new Promise<AdminClearReferralFlagAck>(
          (resolve, reject) => {
            if (!sock.connected) {
              reject(new RpcError("socket_not_connected"));
              return;
            }
            let settled = false;
            const timeout = window.setTimeout(() => {
              if (settled) return;
              settled = true;
              reject(new RpcError("timeout"));
            }, 15_000);
            sock.emit(
              "admin:clear_referral_flag",
              { referralId },
              (a: AdminClearReferralFlagAck) => {
                if (settled) return;
                settled = true;
                window.clearTimeout(timeout);
                resolve(a);
              },
            );
          },
        );
        if (!ack.ok) {
          setError(ack.error);
          return;
        }
        // Splice the updated row in place.
        setRows((current) =>
          current.map((r) => (r.id === referralId ? ack.referral : r)),
        );
        setFlaggedCount((c) => Math.max(0, c - 1));
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [socket],
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / query.pageSize)),
    [total, query.pageSize],
  );

  return (
    <div>
      <h2 style={styles.pageTitle}>Referrals</h2>
      <p style={styles.pageSub}>
        {new Intl.NumberFormat("en-US").format(total)} referrals ·{" "}
        {new Intl.NumberFormat("en-US").format(totalPayoutsTurns)} turns paid
        out lifetime ·{" "}
        <span style={{ color: flaggedCount > 0 ? tokens.destructive : tokens.inkMuted }}>
          {flaggedCount} flagged
        </span>
      </p>

      <div style={styles.controlsRow}>
        <input
          type="text"
          placeholder="Filter by email…"
          value={filterInput}
          onChange={(e) => setFilterInput(e.target.value)}
          style={styles.searchInput}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <label style={controlLabelStyle}>
            Status
            <select
              value={query.status}
              onChange={(e) =>
                setQuery((q) => ({
                  ...q,
                  status: e.target
                    .value as typeof query.status,
                  page: 1,
                }))
              }
              style={styles.select}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label style={controlLabelStyle}>
            Rows per page
            <select
              value={query.pageSize}
              onChange={(e) =>
                setQuery((q) => ({
                  ...q,
                  pageSize: Number(e.target.value) as
                    (typeof PAGE_SIZES)[number],
                  page: 1,
                }))
              }
              style={styles.select}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>When</th>
              <th style={styles.th}>Referrer</th>
              <th style={styles.th}>Referee</th>
              <th style={styles.th}>Status</th>
              <th style={{ ...styles.th, textAlign: "right" }}>Earned</th>
              <th style={styles.th}>Notes</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={7} style={styles.empty}>
                  Loading…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={7} style={{ ...styles.empty, color: tokens.destructive }}>
                  {error}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} style={styles.empty}>
                  No referrals match.
                </td>
              </tr>
            ) : (
              rows.map((r) => <Row key={r.id} row={r} onClearFlag={clearFlag} />)
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.pager}>
        <div>
          Page {query.page} of {totalPages}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() =>
              setQuery((q) => ({ ...q, page: Math.max(1, q.page - 1) }))
            }
            disabled={query.page <= 1 || loading}
            style={{
              ...styles.pagerBtn,
              ...(query.page <= 1 || loading ? styles.pagerBtnDisabled : {}),
            }}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() =>
              setQuery((q) => ({ ...q, page: Math.min(totalPages, q.page + 1) }))
            }
            disabled={query.page >= totalPages || loading}
            style={{
              ...styles.pagerBtn,
              ...(query.page >= totalPages || loading
                ? styles.pagerBtnDisabled
                : {}),
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({
  row,
  onClearFlag,
}: {
  row: AdminReferralRow;
  onClearFlag: (id: string) => void | Promise<void>;
}) {
  const statusPill: CSSProperties =
    row.status === "active"
      ? styles.pillActive
      : row.status === "pending"
        ? styles.pillTrial
        : styles.pillGrey;
  const flaggedPill: CSSProperties = {
    color: tokens.destructive,
    background: tokens.destructiveMuted,
    borderColor: tokens.destructive,
  };
  return (
    <tr>
      <td style={{ ...styles.td, ...styles.tdMono, fontSize: 12 }}>
        {formatWhen(row.attributedAt)}
      </td>
      <td style={styles.td}>
        <div style={emailCellStyle}>{row.referrerEmail ?? "(anonymised)"}</div>
      </td>
      <td style={styles.td}>
        <div style={emailCellStyle}>{row.refereeEmail}</div>
        {row.sourceSlug && (
          <div style={subStyle}>via /s/{row.sourceSlug}</div>
        )}
      </td>
      <td style={styles.td}>
        <span style={{ ...styles.pill, ...statusPill }}>{row.status}</span>
        {row.flagged && (
          <span style={{ ...styles.pill, ...flaggedPill, marginLeft: 4 }}>
            flagged
          </span>
        )}
        {row.frozenAt && (
          <div style={subStyle}>frozen</div>
        )}
      </td>
      <td style={{ ...styles.td, textAlign: "right" }}>
        {row.monthlyBonusTurnsActive}
      </td>
      <td style={styles.td}>
        {row.flagReason ? (
          <div style={subStyle}>{row.flagReason}</div>
        ) : row.cancellationGraceUntil ? (
          <div style={subStyle}>
            grace ends {formatWhen(row.cancellationGraceUntil)}
          </div>
        ) : (
          <div style={subStyle}>—</div>
        )}
      </td>
      <td style={{ ...styles.td, textAlign: "right" }}>
        {row.flagged && (
          <button
            type="button"
            onClick={() => void onClearFlag(row.id)}
            style={clearFlagBtn}
            title="Clear flag — re-enable payouts on next cycle"
          >
            Clear flag
          </button>
        )}
      </td>
    </tr>
  );
}

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return "—";
    return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)}`;
  } catch {
    return iso;
  }
}

const controlLabelStyle: CSSProperties = {
  color: tokens.inkMuted,
  fontSize: 13,
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const emailCellStyle: CSSProperties = {
  color: tokens.inkPrimary,
  fontSize: 13,
};

const subStyle: CSSProperties = {
  color: tokens.inkMuted,
  fontSize: 11,
  marginTop: 2,
};

const clearFlagBtn: CSSProperties = {
  background: "transparent",
  border: `1px solid ${tokens.border}`,
  color: tokens.warn,
  padding: "4px 8px",
  fontSize: 12,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: tokens.fontBody,
};
